import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import { useMainScreen } from '@/hooks/useMainScreen'
import { useOtcStats } from '@/hooks/useOTC'
// import { useUnlocks } from '@/hooks/useUnlocks'
import { useOTC } from '@/hooks/useOTC'
import { useIndustries } from '@/hooks/useIndustries'
import { useAccessTier } from '@/hooks/useAccessTier'
import { OrderbookItem } from '@/types/otc'
import { PageTemplate } from '@/components/templates'
import { CONTAINER_SIZES } from '@/constants'
import { Button } from '@/components/ui/button'
import OrderbookTable from '@/components/orderbook/OrderbookTable'
import { OrderInquiryModal } from '@/components/organisms/modals'
import { BrowseModeBanner, GatedPanel, useEnterPhaseTwo } from '@/components/gate'
import { useProfile } from '@/hooks/useProfile'
import { TrendingAssets, TopDiscountsToday, Categories, /* UpcomingUnlocks, */ MarketIndicator, DashboardStats } from '@/components/molecules'
import { formStyles } from '@/constants/styles'
import { useIsMobile } from '@/hooks/use-mobile'

const MainScreen = () => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { canReadFinancials } = useAccessTier()
  const enterPhaseTwo = useEnterPhaseTwo()
  const { data, loading, error } = useMainScreen()
  const { data: otcStats, loading: otcStatsLoading } = useOtcStats()
  const { profile } = useProfile()
  const { industries, loading: industriesLoading } = useIndustries()
  const [orderInquiryModal, setOrderInquiryModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<OrderbookItem | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  // Set first industry as selected when industries are loaded
  useEffect(() => {
    if (industries.length > 0 && !selectedCategory) {
      setSelectedCategory(industries[0])
    }
  }, [industries, selectedCategory])

  // Fetch all orders once (without industry filter) to avoid re-fetching on category change.
  // `useOTC` picks the endpoint from the access tier, so this is the public,
  // identity-only listing for a browse-tier session.
  const allCategoryOrdersParams = useMemo(() => ({
    limit: 50 // Load enough orders to cover all categories
  }), [])

  const {
    orders: allCategoryOrders,
    loading: categoryOrdersLoading,
    total: totalListedOrders,
  } = useOTC(allCategoryOrdersParams)

  // Fetch upcoming unlocks - load more than 4 to enable scrolling, but show only 4 at a time
  // const upcomingUnlocksParams = useMemo(() => ({ limit: 20 })) // Load more for scrolling
  // const { unlocks: upcomingUnlocks, loading: upcomingUnlocksLoading } = useUnlocks(upcomingUnlocksParams)

  // Filter and memoize category items on client side to prevent unnecessary re-renders
  const categoryItems = useMemo(() => {
    const filtered = selectedCategory
      ? allCategoryOrders.filter(order => order.industry === selectedCategory)
      : allCategoryOrders

    return filtered.slice(0, 4).map(order => ({
      ...order,
      category: order.industry || selectedCategory
    }))
  }, [allCategoryOrders, selectedCategory])

  /**
   * Clicking an order.
   *
   * 🔴 For a browse-tier user this must NOT open the inquiry modal: the modal
   * prints the order's `offered_amount` and FDV, and its whole purpose is to
   * start a trade. Route into phase 2 instead, remembering where they were so
   * finishing brings them back here.
   */
  const handleOrderClick = (order: OrderbookItem) => {
    if (!canReadFinancials) {
      enterPhaseTwo()
      return
    }
    setSelectedOrder(order)
    setOrderInquiryModal(true)
  }

  const handleInquirySubmitted = () => {
    // Refresh data or show success message
    if (process.env.NODE_ENV === 'development') {
    console.log('Order inquiry submitted successfully')
    }
  }

  if (loading) {
    return <PageTemplate isLoading />
  }

  if (error) {
    return (
      <PageTemplate>
        <div className="text-center py-8">
          <p className="text-red-500">{error}</p>
          <Button
            onClick={() => window.location.reload()}
            className="mt-4"
          >
            Try Again
          </Button>
        </div>
      </PageTemplate>
    )
  }

  // Top bar metrics from /orderbook/stats.
  //
  // 🔴 These are `?? null`, NOT `|| 0`. The old `|| 0` turned every failure —
  // a 403, a 500, a timeout — into `$0 total orderbook value, 0 projects,
  // 0.0%`, rendered with the same confidence as a real figure. A user was told
  // the marketplace was EMPTY when in fact nothing had been fetched, which is
  // worse than showing nothing: it is a wrong answer presented as a right one.
  // `null` means "no figure", and every tile renders a placeholder for it.
  //
  // A browse-tier session never requests these at all (`useOtcStats` is
  // disabled), so they are null by construction rather than by failure — the
  // tiles render the lock either way.
  const totalProjects = otcStats?.total_projects ?? null
  const totalOrderbookValue = otcStats?.total_orderbook_value ?? null
  const buyOrders = otcStats?.buy_orders ?? null
  const sellOrders = otcStats?.sell_orders ?? null
  const priceSpread = otcStats?.price_spread ?? null
  const dealSpread = otcStats?.deal_spread ?? null

  // Average Discount - API provides this as a percentage
  const averageDiscount = otcStats?.assets_on_discount ?? null

  // Calculate buyer percentage for greed/fear indicator, from buy_orders vs
  // sell_orders. Null when either side is unknown — a 50/50 "Neutral" default
  // would be an invented market reading.
  const totalOrders = buyOrders !== null && sellOrders !== null ? buyOrders + sellOrders : null
  const buyerPercentage =
    totalOrders !== null && totalOrders > 0 && buyOrders !== null
      ? Math.round((buyOrders / totalOrders) * 100)
      : null
  const sentiment =
    buyerPercentage === null
      ? null
      : buyerPercentage >= 60
        ? 'Greed'
        : buyerPercentage <= 40
          ? 'Fear'
          : 'Neutral'
  const discountedItems = data?.discountedAssets
    ?.sort((a, b) => Math.abs(b.discount || 0) - Math.abs(a.discount || 0))
    ?.map((asset) => {
      const discountPercent = Math.abs(asset.discount || 0)

      let tag: 'hot' | 'trending' | 'most_profitable' = 'trending'
      if (discountPercent >= 30) {
        tag = 'hot'
      } else if (discountPercent >= 25) {
        tag = 'most_profitable'
      } else {
        tag = 'trending'
      }

      return {
        ...asset,
        discount: discountPercent,
        tag
      }
    }) || []

  /**
   * The trending strip for a browse-tier user.
   *
   * `/orderbook/trending` has no non-financial counterpart, so there is no
   * ranked list to show. Rather than blank the strip, it is filled from the
   * public listing the page has already fetched — real rows, real companies,
   * amounts absent. The heading changes with it: calling an unranked slice
   * "Trending" would be a small lie, and the point of this work is not to
   * present things that are not true.
   */
  const trendingAssets = canReadFinancials
    ? (data?.trendingAssets.slice(0, 15) ?? [])
    : allCategoryOrders.slice(0, 15)
  const trendingTitle = canReadFinancials ? undefined : 'Assets on SecondLane'

  return (
    <div
      className="relative min-h-screen overflow-x-hidden"
      style={{
        background: 'var(--page-gradient)'
      }}
    >
      <PageTemplate
        maxWidth={CONTAINER_SIZES.WIDE}
      >
        <div className="space-y-4 md:space-y-6 relative" style={{ zIndex: 10 }}>
        {/* One explanation at the top, rather than the same sentence repeated
            inside every locked panel below. */}
        {!canReadFinancials && <BrowseModeBanner />}

        {/* Dashboard Stats Section */}
        <DashboardStats
          profile={profile || undefined}
          totalProjects={totalProjects}
          totalOrderbookValue={totalOrderbookValue}
          averageDiscount={averageDiscount}
          buyOrders={buyOrders}
          sellOrders={sellOrders}
              loading={otcStatsLoading}
          financialsHidden={!canReadFinancials}
          listedOrders={totalListedOrders}
          totalProjectsMonthlyAvg={otcStats?.total_projects_monthly_avg}
          orderbookMonthlyAvg={otcStats?.orderbook_monthly_avg}
                />

        {/* Trending Assets Section */}
        <TrendingAssets
          assets={trendingAssets}
          title={trendingTitle}
          financialsHidden={!canReadFinancials}
          onAssetClick={handleOrderClick}
          loading={canReadFinancials ? loading : categoryOrdersLoading}
        />

        {isMobile ? (
          <>
            <div className={`${formStyles.cardNoShadow} !p-0 !rounded-none flex flex-col w-full h-auto`}>
              {canReadFinancials ? (
                <TopDiscountsToday
                  loading={loading}
                  items={discountedItems}
                  onItemClick={(item) => {
                    if (item.project_id) {
                      navigate(`/projects/${item.project_id}`)
                    }
                  }}
                  onBuyClick={handleOrderClick}
                />
              ) : (
                <GatedPanel
                  title="Top discounts today"
                  description="A discount is a price against a valuation. Verify your institution to see today's."
                  className="min-h-[280px]"
                />
              )}
            </div>

            <div className={`${formStyles.cardNoShadow} !p-0 !rounded-none flex flex-col w-full h-auto`}>
              <Categories
                items={categoryItems}
                categories={industries}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                onItemClick={handleOrderClick}
                loading={industriesLoading || categoryOrdersLoading}
              />
            </div>


            <OrderbookTable
              title="Secondaries Orders"
              navigate={navigate}
              useDataHook={useOTC}
              dataKey="orders"
              showPagination={true}
              showViewSecondariesMarket={true}
              useUrlParams={true}
              showOrderInquiry={true}
            />

            <div className={`${formStyles.cardNoShadow} dark:!bg-[rgba(28,28,28,0.42)] !p-0 !rounded-none flex flex-col w-full h-auto`}>
              {canReadFinancials ? (
                <MarketIndicator
                  buyerPercentage={buyerPercentage}
                  sentiment={sentiment}
                  priceSpread={priceSpread}
                  dealSpread={dealSpread}
                  loading={otcStatsLoading}
                />
              ) : (
                <GatedPanel
                  title="Market indicator"
                  description="Buy/sell sentiment and the spreads between them are priced signals."
                  className="min-h-[280px]"
                />
              )}
            </div>
          </>
        ) : (
          <>
            {/* Dashboard Blocks Section - desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
              <div className="lg:col-span-2 flex h-full">
                {/* The gated panel reserves the exact same h-[388px] as the real
                    one, so the dashboard does not reflow when a user attests. */}
                <div className={`${formStyles.cardNoShadow} !p-0 !rounded-none flex flex-col w-full h-[388px]`}>
                  {canReadFinancials ? (
                    <TopDiscountsToday
                      loading={loading}
                      items={discountedItems}
                      onItemClick={(item) => {
                        if (item.project_id) {
                          navigate(`/projects/${item.project_id}`)
                        }
                      }}
                      onBuyClick={handleOrderClick}
                    />
                  ) : (
                    <GatedPanel
                      title="Top discounts today"
                      description="A discount is a price against a valuation. Verify your institution to see today's."
                      className="h-full"
                    />
                  )}
                </div>
              </div>

              <div className="lg:col-span-1 flex h-full">
                <div className={`${formStyles.cardNoShadow} !p-0 !rounded-none flex flex-col w-full h-[388px]`}>
                  <Categories
                    items={categoryItems}
                    categories={industries}
                    selectedCategory={selectedCategory}
                    onCategoryChange={setSelectedCategory}
                    onItemClick={handleOrderClick}
                    loading={industriesLoading || categoryOrdersLoading}
                  />
                </div>
              </div>


              <div className="lg:col-span-2 flex h-full">
                <div className={`${formStyles.cardNoShadow} dark:!bg-[rgba(28,28,28,0.42)] !p-0 !rounded-none flex flex-col w-full h-[336px]`}>
                  {canReadFinancials ? (
                    <MarketIndicator
                      buyerPercentage={buyerPercentage}
                      sentiment={sentiment}
                      priceSpread={priceSpread}
                      dealSpread={dealSpread}
                      loading={otcStatsLoading}
                    />
                  ) : (
                    <GatedPanel
                      title="Market indicator"
                      description="Buy/sell sentiment and the spreads between them are priced signals."
                      className="h-full"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Orders Table Section */}
            <OrderbookTable
              title="Secondaries Orders"
              navigate={navigate}
              useDataHook={useOTC}
              dataKey="orders"
              showPagination={true}
              showViewSecondariesMarket={true}
              useUrlParams={true}
              showOrderInquiry={true}
            />
          </>
        )}
      </div>

      {/* Order Inquiry Modal. Only ever opened by `handleOrderClick`, which
          refuses to open it without `canReadFinancials`. */}
      <OrderInquiryModal
        isOpen={orderInquiryModal}
        onClose={() => {
          setOrderInquiryModal(false)
          setSelectedOrder(null)
        }}
        order={selectedOrder}
        onInquirySubmitted={handleInquirySubmitted}
      />
      </PageTemplate>
    </div>
  )
}

export default MainScreen
